import {clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll} from "body-scroll-lock";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Arrow from "../../../assets/svg/arrow.svg";
import useWatchOutside from "../../../hooks/useWatchOutside";
import Media from "../../Common/Media";
import Button from "../../UI/Button";
import Link from "../../UI/Link";
import * as navStyles from "./navbar.module.css";
import SubmenuMega from "./ui/SubmenuMega";

const isBrowser = typeof window !== "undefined";

const NavBar = ({isMobileMenuOpened, menu, linkToLK}) => {
    const menuRef = useRef(null);

    const [openedDropdownIndex, setOpenedDropdownIndex] = useState(-1);

    useEffect(() => {
        if (isMobileMenuOpened && menuRef.current) {
            disableBodyScroll(menuRef.current, {reserveScrollBarGap: true});

            return;
        }

        if (menuRef.current) {
            enableBodyScroll(menuRef.current);
        }

        setOpenedDropdownIndex(-1);
        clearAllBodyScrollLocks();
    }, [isMobileMenuOpened]);

    useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        };
    }, []);

    const toggleDropdown = useCallback((index, isFocusHandler = false) => {
        setOpenedDropdownIndex((currentIndex) => {
            if (!isFocusHandler && currentIndex === index) {
                return -1;
            }

            return index;
        });
    }, []);

    useWatchOutside(
        menuRef,
        useCallback(() => toggleDropdown(-1), [toggleDropdown]),
    );

    const getNavSection = (entry, idx, isLast = false) => {
        const getLink = (link, linkIdx) => {
            return (
                <li key={linkIdx}>
                    <Link
                        to={/^\/(?!\/)/.test(link.url) ? `${entry.url || ""}${link.url}` : link.url}
                        withArrow={false}
                        className={navStyles.nav__submenuLink}
                        activeClassName={navStyles.nav__submenuLink_active}
                        partiallyActive
                        inNewTab={link.newTab}
                    >
                        <div className={navStyles.nav__submenuLinkContent}>
                            {link.icon && (
                                <Media
                                    media={{content: link.icon, youtubeVideoURL: ""}}
                                    className={navStyles.nav__submenuLinkIcon}
                                />
                            )}
                            <div>
                                <p className={navStyles.nav__submenuLinkTitle}>{link.title}</p>
                                {link.description && (
                                    <p className={navStyles.nav__submenuLinkDesc}>{link.description}</p>
                                )}
                            </div>
                        </div>
                    </Link>
                </li>
            );
        };

        return (
            <div key={idx}>
                {entry.title && <p className={navStyles.nav__submenuTitle}>{entry.title}</p>}
                <ul className={navStyles.nav__submenuList}>
                    {entry.links.map(getLink)}
                    {isLast && !!linkToLK && (
                        <Button isLink href={linkToLK} variant="primary" className={navStyles.nav__button}>
                            Войти в личный кабинет
                        </Button>
                    )}{" "}
                </ul>
            </div>
        );
    };

    const getNavItem = (entry, idx, arr) => {
        // Если нет подменю и нет items, или items пусто - показываем как обычную ссылку
        if ((!entry.section && !entry.items) || (entry.items && entry.items.length === 0)) {
            return (
                <li className={navStyles.nav__item} key={idx}>
                    <Link
                        className={navStyles.nav__link}
                        activeClassName={navStyles.nav__link_active}
                        partiallyActive
                        to={entry?.link?.url || entry.url}
                        withArrow={false}
                        inNewTab={entry?.link?.newTab || entry.newTab}
                    >
                        {entry.title}
                    </Link>
                </li>
            );
        }

        // Для старого меню проверяем isActive по section
        let isActive = false;
        if (entry.section && entry.section.length > 0) {
            isActive = entry.section.reduce((acc, {url, links}) => {
                if (!isBrowser) {
                    return acc;
                }

                const haveActiveLink = links.reduce((containActiveLink, {url: linkUrl}) => {
                    const urlToTest = /^\/(?!\/)/.test(linkUrl) ? `${url || ""}${linkUrl}` : linkUrl;

                    if (containActiveLink) {
                        return containActiveLink;
                    }

                    if (!isBrowser) {
                        return false;
                    }

                    const doesURLContainsURLToTest = window.location.href.indexOf(urlToTest) > 0;
                    if (!doesURLContainsURLToTest) {
                        return false;
                    }

                    const urlParts = window.location.href.split(urlToTest);
                    return urlParts[1].length === 0;
                }, false);

                return acc || haveActiveLink;
            }, false);
        }

        return (
            <li
                className={`${navStyles.nav__item}${
                    openedDropdownIndex === idx ? ` ${navStyles.nav__item_opened}` : ""
                } ${
                    entry.source === "new" && entry.items && entry.items.length > 0
                        ? `${navStyles.nav__item__mega}`
                        : ""
                }`}
                key={idx}
            >
                <button
                    className={`${navStyles.nav__link}${isActive ? ` ${navStyles.nav__link_active}` : ""}`}
                    type="button"
                    onClick={() => toggleDropdown(idx)}
                >
                    {entry.title} <Arrow />
                </button>
                <div
                    className={`${navStyles.nav__submenu}${
                        idx === arr.length - 1 ? ` ${navStyles.nav__submenu_last}` : ""
                    }`}
                    onFocusCapture={() => toggleDropdown(idx, true)}
                >
                    {/* Для нового меню (source: 'new') с items - отрисовываем SubmenuMega */}
                    {entry.source === "new" && entry.items && entry.items.length > 0 && (
                        <SubmenuMega menuItems={entry.items} />
                    )}

                    {/* Для старого меню (source: 'old') с section - отрисовываем обычное подменю */}
                    {entry.source === "old" &&
                        entry.section &&
                        entry.section.map((submenuItem, submenuIdx) => {
                            const isLast = arr.length - 1 === idx;
                            return getNavSection(submenuItem, submenuIdx, isLast);
                        })}

                    {/* Для нового меню с пустыми items - показываем как обычное подменю (если есть section) */}
                    {entry.source === "new" &&
                        (!entry.items || entry.items.length === 0) &&
                        entry.section &&
                        entry.section.map((submenuItem, submenuIdx) => {
                            const isLast = arr.length - 1 === idx;
                            return getNavSection(submenuItem, submenuIdx, isLast);
                        })}
                </div>
            </li>
        );
    };

    return (
        <nav>
            <ul className={`${navStyles.nav} ${isMobileMenuOpened ? navStyles.nav_opened : ""}`} ref={menuRef}>
                {menu.map(getNavItem)}
            </ul>
        </nav>
    );
};

export default NavBar;
